import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { poolServiceProviderInfoService } from '../../../../services/poolServiceProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('PoolServiceProviderInput');

export const PoolServiceProviderInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { poolServiceProviderId?: string; isEdit?: boolean }>, string>>();
  const { poolServiceProviderId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showServiceFrequencyModal, setShowServiceFrequencyModal] = useState(false);
  const [showPoolTypeModal, setShowPoolTypeModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedPoolServiceProviderId, setSavedPoolServiceProviderId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    companyName: '',
    accountNumber: '',
    technicianName: '',
    phoneNumber: '',
    emergencyPhone: '',
    email: '',
    serviceAddress: '',
    servicesProvided: '',
    serviceFrequency: '',
    costPerService: '',
    seasonalService: false,
    seasonStartDate: '',
    seasonEndDate: '',
    poolType: '',
    poolSize: '',
    equipmentMaintained: '',
    chemicalsUsed: '',
    emergencyService: false,
    notes: '',
    username: '',
    password: '',
  });

  const serviceFrequencies = ['Weekly', 'Bi-weekly', 'Monthly', 'Seasonal', 'As Needed', 'Other'];
  const poolTypes = ['Chlorine', 'Saltwater', 'Natural', 'Fiberglass', 'Vinyl', 'Other'];

  // Load existing pool service provider data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'poolServiceProviderId:', poolServiceProviderId);
    if (isEdit && poolServiceProviderId) {
      logger.debug('[Edit Debug] Loading pool service provider data for editing');
      loadPoolServiceProviderData();
    }
  }, [isEdit, poolServiceProviderId]);

  const loadPoolServiceProviderData = async () => {
    try {
      logger.debug('[Edit Debug] loadPoolServiceProviderData called with ID:', poolServiceProviderId);
      if (!poolServiceProviderId) {
        logger.debug('[Edit Debug] No poolServiceProviderId provided, returning');
        return;
      }
      
      logger.debug('[Edit Debug] Fetching data for ID:', poolServiceProviderId);
      const data = await poolServiceProviderInfoService.getPoolServiceProviderInfoById(poolServiceProviderId);
      logger.debug('[Edit Debug] Received data:', data);
      
      if (data) {
        setFormData({
          companyName: data.companyName || '',
          accountNumber: '',  // Will be loaded from vault
          technicianName: data.technicianName || '',
          phoneNumber: formatPhoneNumber(data.phoneNumber || ''),
          emergencyPhone: formatPhoneNumber(data.emergencyPhone || ''),
          email: data.email || '',
          serviceAddress: data.serviceAddress || '',
          servicesProvided: data.servicesProvided || '',
          serviceFrequency: data.serviceFrequency || '',
          costPerService: data.costPerService ? formatCurrency(data.costPerService.toString()) : '',
          seasonalService: data.seasonalService || false,
          seasonStartDate: data.seasonStartDate || '',
          seasonEndDate: data.seasonEndDate || '',
          poolType: data.poolType || '',
          poolSize: data.poolSize || '',
          equipmentMaintained: data.equipmentMaintained || '',
          chemicalsUsed: data.chemicalsUsed || '',
          emergencyService: data.emergencyService || false,
          notes: data.notes || '',
          username: '',
          password: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(poolServiceProviderId, 'pool-service-provider');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
              accountNumber: portalData.accountNumber || '',
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('[Edit Debug] Error loading portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading pool service provider data:', error);
      Alert.alert('Error', 'Failed to load pool service provider information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Format date as DD/MM/YYYY
  const formatDate = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    let formatted = '';
    
    if (numericValue.length <= 2) {
      formatted = numericValue;
    } else if (numericValue.length <= 4) {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
    } else {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2, 4) + '/' + numericValue.slice(4, 8);
    }
    
    return formatted;
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, costPerService: formatCurrency(cleaned) });
  };

  const handleServiceFrequencySelect = (frequency: string) => {
    setFormData({ ...formData, serviceFrequency: frequency });
    setShowServiceFrequencyModal(false);
  };

  const handlePoolTypeSelect = (type: string) => {
    setFormData({ ...formData, poolType: type });
    setShowPoolTypeModal(false);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.companyName) {
        Alert.alert('Validation Error', 'Company name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        companyName: formData.companyName,
        technicianName: formData.technicianName || undefined,
        phoneNumber: formData.phoneNumber.replace(/\D/g, '') || undefined,
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, '') || undefined,
        email: formData.email || undefined,
        serviceAddress: formData.serviceAddress || undefined,
        servicesProvided: formData.servicesProvided || undefined,
        serviceFrequency: formData.serviceFrequency || undefined,
        costPerService: formData.costPerService ? parseFloat(formData.costPerService.replace(/[^0-9.]/g, '')) : undefined,
        seasonalService: formData.seasonalService,
        seasonStartDate: formData.seasonStartDate || undefined,
        seasonEndDate: formData.seasonEndDate || undefined,
        poolType: formData.poolType || undefined,
        poolSize: formData.poolSize || undefined,
        equipmentMaintained: formData.equipmentMaintained || undefined,
        chemicalsUsed: formData.chemicalsUsed || undefined,
        emergencyService: formData.emergencyService,
        notes: formData.notes || undefined,
      };

      // Create or update pool service provider record
      let saved;
      let savedId;

      if (isEdit && poolServiceProviderId) {
        saved = await poolServiceProviderInfoService.updatePoolServiceProviderInfo(poolServiceProviderId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = saved?.data || saved;
        savedId = updatedData.id || poolServiceProviderId;
      } else {
        saved = await poolServiceProviderInfoService.createPoolServiceProviderInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }
      
      // Store the saved ID for the Done button
      setSavedPoolServiceProviderId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'pool-service-provider', {
            username: formData.username || undefined,
            password: formData.password || undefined,
            accountNumber: formData.accountNumber || undefined,
          });
        } catch (e) {
          logger.error('Vault write failed for pool service provider portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit, go to lock/unlock screen
        Alert.alert('Success', 'Pool service provider information updated successfully!', [
          { text: 'OK', onPress: () => {
            (navigation as any).navigate('PoolServiceProviderLockUnlock', { poolServiceProviderId: savedId });
          }}
        ]);
        return;
      }

      // Clear the form and reset states for Add Another
      setFormData({
        companyName: '',
        accountNumber: '',
        technicianName: '',
        phoneNumber: '',
        emergencyPhone: '',
        email: '',
        serviceAddress: '',
        servicesProvided: '',
        serviceFrequency: '',
        costPerService: '',
        seasonalService: false,
        seasonStartDate: '',
        seasonEndDate: '',
        poolType: '',
        poolSize: '',
        equipmentMaintained: '',
        chemicalsUsed: '',
        emergencyService: false,
        notes: '',
        username: '',
        password: '',
      });
      setHasOnlinePortal(false);
      
      // Show Add Another / Done buttons after successful save
      setShowAddAnotherDone(true);
      // Reset submitting so buttons are interactive
      setSubmitting(false);
    } catch (error: any) {
      logger.error('Error saving pool service provider info:', error);
      Alert.alert('Error', 'Failed to save pool service provider information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      accountNumber: '',
      technicianName: '',
      phoneNumber: '',
      emergencyPhone: '',
      email: '',
      serviceAddress: '',
      servicesProvided: '',
      serviceFrequency: '',
      costPerService: '',
      seasonalService: false,
      seasonStartDate: '',
      seasonEndDate: '',
      poolType: '',
      poolSize: '',
      equipmentMaintained: '',
      chemicalsUsed: '',
      emergencyService: false,
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedPoolServiceProviderId);
    if (savedPoolServiceProviderId) {
      (navigation as any).navigate('PoolServiceProviderLockUnlock', { poolServiceProviderId: savedPoolServiceProviderId });
    } else {
      Alert.alert('Error', 'No pool service provider ID found. Please try again.');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Pool Service Provider Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Ex: Blue Haven Pools, Leslie's Pool Service"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Technician Name</Text>
            <TextInput
              style={styles.input}
              value={formData.technicianName}
              onChangeText={(text) => setFormData({ ...formData, technicianName: text })}
              placeholder="Your regular technician's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.phoneNumber}
              onChangeText={(text) => handlePhoneChange('phoneNumber', text)}
              placeholder="(555) 555-5555"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => handlePhoneChange('emergencyPhone', text)}
              placeholder="(555) 555-5555"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text })}
              placeholder="Ex: service@bluehaven.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - After email */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your pool service provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials and account number. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Account Number 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.accountNumber}
                    onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                    placeholder="Enter Account Number"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showAccountNumber}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowAccountNumber(!showAccountNumber)}
                  >
                    <Image
                      source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Address where pool service is provided"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={2}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Frequency</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowServiceFrequencyModal(true)}
            >
              <Text style={[styles.inputText, !formData.serviceFrequency && styles.placeholderText]}>
                {formData.serviceFrequency || 'Select service frequency'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Services Provided</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.servicesProvided}
              onChangeText={(text) => setFormData({ ...formData, servicesProvided: text })}
              placeholder="Ex: Pool cleaning, chemical balancing, equipment maintenance"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pool Type</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowPoolTypeModal(true)}
            >
              <Text style={[styles.inputText, !formData.poolType && styles.placeholderText]}>
                {formData.poolType || 'Select pool type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pool Size</Text>
            <TextInput
              style={styles.input}
              value={formData.poolSize}
              onChangeText={(text) => setFormData({ ...formData, poolSize: text })}
              placeholder="Ex: 20x40 feet, 18,000 gallons"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cost Per Service</Text>
            <TextInput
              style={styles.input}
              value={formData.costPerService ? `$${formData.costPerService}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.toggleText}>Seasonal Service</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={formData.seasonalService ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={(value) => setFormData({ ...formData, seasonalService: value })}
              value={formData.seasonalService}
            />
          </View>

          {formData.seasonalService && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Season Start Date</Text>
                <TextInput
                  style={styles.input}
                  value={formData.seasonStartDate}
                  onChangeText={(text) => setFormData({ ...formData, seasonStartDate: formatDate(text) })}
                  placeholder="DD/MM/YYYY"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="numeric"
                  maxLength={10}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Season End Date</Text>
                <TextInput
                  style={styles.input}
                  value={formData.seasonEndDate}
                  onChangeText={(text) => setFormData({ ...formData, seasonEndDate: formatDate(text) })}
                  placeholder="DD/MM/YYYY"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="numeric"
                  maxLength={10}
                />
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Equipment Maintained</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.equipmentMaintained}
              onChangeText={(text) => setFormData({ ...formData, equipmentMaintained: text })}
              placeholder="Ex: Pumps, filters, heaters, automatic cleaners"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Chemicals Used</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.chemicalsUsed}
              onChangeText={(text) => setFormData({ ...formData, chemicalsUsed: text })}
              placeholder="Ex: Chlorine, pH balancers, algaecides"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.toggleText}>Emergency Service Available</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={formData.emergencyService ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={(value) => setFormData({ ...formData, emergencyService: value })}
              value={formData.emergencyService}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={styles.addAnotherButton} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.doneButton} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      {/* Service Frequency Modal */}
      <Modal
        visible={showServiceFrequencyModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowServiceFrequencyModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Service Frequency</Text>
              <TouchableOpacity
                onPress={() => setShowServiceFrequencyModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {serviceFrequencies.map((frequency) => (
                <TouchableOpacity
                  key={frequency}
                  style={styles.stateItem}
                  onPress={() => handleServiceFrequencySelect(frequency)}
                >
                  <Text style={styles.stateItemText}>{frequency}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Pool Type Modal */}
      <Modal
        visible={showPoolTypeModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowPoolTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Pool Type</Text>
              <TouchableOpacity
                onPress={() => setShowPoolTypeModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {poolTypes.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handlePoolTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginRight: ms(16),
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});
